import * as React from "react";
import { Search, X, Loader, Edit2Icon } from "lucide-react";
import { useGetAllQuery } from "../store/studentApi";
import { useSelector } from "react-redux";
import { RootState, Student } from "@/types";
import { Link, useParams, useSearchParams } from "react-router-dom";
import ActionDropdown from "./ActionDropdown";
import { Input } from "./ui/input";
import FeeSummaryCard from "./FeeSummaryCard";
import { useGetAllFeeQuery } from "@/store/feeApi";
import { Button } from "./ui/button";

const getInitials = (fname?: string, lname?: string) => {
  const firstInitial = fname ? fname.charAt(0).toUpperCase() : "";
  const lastInitial = lname ? lname.charAt(0).toUpperCase() : "";
  return `${firstInitial}${lastInitial}` || "?";
};

const StudentList = () => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = React.useState(
    searchParams.get("status") || "all"
  );

  const [currentPage, setCurrentPage] = React.useState(1);
  const studentsPerPage = 10;

  const { user } = useSelector((state: RootState) => state.user);
  const { data: students, isLoading } = useGetAllQuery(user?.token);
  console.log(students , "studentss object")

  const { data: Fee } = useGetAllFeeQuery(user?.token);

  const { coursename } = useParams();

  const course = students?.filter(
    (student: Student) =>
      student.course.toLowerCase() === coursename?.toLowerCase()
  );

  const filteredStudents = React.useMemo(() => {
    const studentList = coursename ? course : students;
    return studentList
      ?.filter((student: Student) => {
        const matchesSearch =
          student.fname.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.lname.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus =
          statusFilter === "all" ||
          student.status === statusFilter ||
          student.isPlaced.toLowerCase() === statusFilter;
        return matchesSearch && matchesStatus;
      })
      .reverse();
  }, [students, searchTerm, statusFilter, course, coursename]);

  const totalStudents = filteredStudents?.length || 0;
  const totalPages = Math.ceil(totalStudents / studentsPerPage);
  const startIndex = (currentPage - 1) * studentsPerPage;
  const currentStudents = filteredStudents?.slice(
    startIndex,
    startIndex + studentsPerPage
  );

  const totalAmounts = React.useMemo(() => {
    if (!students || !Fee) {
      return { paid: 0, payable: 0 };
    }

    const filteredFees = filteredStudents.flatMap(
      (student: { _id: string }) => {
        if (!student?._id) return [];

        return Fee.filter(
          (feeObj: { student: string }) => feeObj?.student === student._id
        )
          .map((fee: { [key: string]: unknown }) => ({
            ...fee,
            student,
          }))
          .filter(Boolean);
      }
    );

    const totals = filteredFees.reduce(
      (
        acc: { paid: number; payable: number },
        fee: { paid: number; payable: number }
      ) => ({
        paid: acc.paid + (Number(fee?.paid) || 0),
        payable: acc.payable + (Number(fee?.payable) || 0),
      }),
      { paid: 0, payable: 0 }
    );

    return totals;
  }, [filteredStudents, Fee, students]);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  // Count students for each status
  const statusCounts = React.useMemo(() => {
    if (!students) return { all: 0, active: 0, dropped: 0, placed: 0 };

    return students.reduce(
      (
        counts: {
          active: number;
          dropped: number;
          placed: number;
          all: number;
        },
        student: Student
      ) => {
        if (student.status === "Active") counts.active++;
        if (student.status === "Disable") counts.dropped++;
        if (student.isPlaced?.toLowerCase() === "yes") counts.placed++;
        counts.all++;
        return counts;
      },
      { all: 0, active: 0, dropped: 0, placed: 0 }
    );
  }, [students]);

  const getRankGrade = (score) => {
    if (!score) return "-";
    if (score >= 90) return "S";
    if (score >= 80) return "A";
    if (score >= 70) return "B";
    if (score >= 60) return "C";
    if (score >= 50) return "D";
    return "F";
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-4 space-y-4">
      {/* {/ {/ Header Section /} /} */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="relative w-full sm:w-72 group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 transition-colors group-hover:text-blue-500" />
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search students..."
              className="w-full text-foreground pl-10 pr-10 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {coursename && (
          <button
            className={`px-3 py-1 rounded-full text-sm transition-colors border border-green-400 ${"bg-white-100 text-green-800"}`}
          >
            {coursename}
            <span className="bg-green-100 text-black px-2 rounded-full">
              {course.length}
            </span>
          </button>
        )}

        {/* {/ {/ Filters Section /} /} */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out`}
        >
          <div className="p-4 rounded-lg space-y-4 bg-transparent">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setStatusFilter("all")}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  statusFilter === "all"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-white border"
                }`}
              >
                All{" "}
                <span className="bg-blue-100 text-blue-800 px-2 rounded-full">
                  {statusCounts.all}
                </span>
              </button>
              <button
                onClick={() => setStatusFilter("Active")}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  statusFilter === "Active"
                    ? "bg-green-100 text-green-800"
                    : "bg-white border"
                }`}
              >
                Active{" "}
                <span className="bg-green-100 text-green-800 px-2 rounded-full">
                  {statusCounts.active}
                </span>
              </button>
              <button
                onClick={() => setStatusFilter("Disable")}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  statusFilter === "Disable"
                    ? "bg-red-100 text-red-600"
                    : "bg-white border"
                }`}
              >
                Dropped{" "}
                <span className="bg-red-100 text-black px-2 rounded-full">
                  {statusCounts.dropped}
                </span>
              </button>
              <button
                onClick={() => setStatusFilter("yes")}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  statusFilter === "yes"
                    ? "bg-red-100 text-red-800"
                    : "bg-white border"
                }`}
              >
                Placed{" "}
                <span className="bg-red-100 text-red-800 px-2 rounded-full">
                  {statusCounts.placed}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* {/ {/ Table Section /} /} */}
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Course Details
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>

                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Fee
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Paid
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pending
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                {!coursename ? (
                  <>
                    {" "}
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </>
                ) : (
                  <></>
                )}
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading ? (
                <tr>
                  <td colSpan={8}>
                    <div className="flex items-center justify-center text-center h-32">
                      <Loader className="h-5 w-5 animate-spin text-center" />
                    </div>
                  </td>
                </tr>
              ) : (
                currentStudents?.map((student: Student, index: number) => {
                  const studentFee = Fee?.filter(
                    (fee: { student: string }) => fee?.student === student._id
                  );
                  const totalPaid =
                    studentFee?.reduce(
                      (acc: number, fee: { paid: number }) =>
                        acc + (Number(fee.paid) || 0),
                      0
                    ) || 0;
                  const totalPayable =
                    studentFee?.reduce(
                      (acc: number, fee: { payable: number }) =>
                        acc + (Number(fee.payable) || 0),
                      0
                    ) || 0;
                  const totalPending = totalPayable - totalPaid;
                  return (
                    <tr
                      key={student._id}
                      className="hover:bg-muted bg-background transition-colors group"
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {startIndex + index + 1}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full dark:border-foreground border overflow-hidden text-foreground flex items-center justify-center bg-zinc-100 dark:bg-zinc-800 shrink-0">
                            <Link to={`/${student?.username}`} className="w-full h-full flex items-center justify-center">
                              {student?.profile ? (
                                <img
                                  src={student?.profile}
                                  alt="profile"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <span className="text-sm font-semibold text-zinc-600 dark:text-zinc-300">
                                  {getInitials(student?.fname, student?.lname)}
                                </span>
                              )}
                            </Link>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-foreground">
                              {student.fname} {student.lname}
                            </span>
                            <br />
                            <span className="text-sm text-gray-500">
                              {student.email}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm text-foreground">
                            {student.course}
                          </span>
                          <span className="text-sm text-gray-500">
                            Batch: {student.batch}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                        {student.contact}
                      </td>

                      <td
                        className={`px-6 py-4 text-right ${
                          student?.status === "Disable"
                            ? "text-red-400"
                            : "text-green-600"
                        }`}
                      >
                        {student?.status}
                      </td>

                      <td className="px-6 py-4 text-right text-green-600">
                        ₹{totalPayable.toLocaleString()}
                      </td>

                      <td className="px-6 py-4 text-right text-foreground">
                        ₹{totalPaid.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-right text-red-600">
                        ₹{totalPending.toLocaleString()}
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-sm ">
                        {student?.totalScore
                          ? getRankGrade(student.totalScore)
                          : student?.rank || "-"}
                      </td>

                      {!coursename ? (
                        <>
                          {" "}
                          <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                            {user?.role === "hr" ? (
                              <Link to={`update/${student._id}`}>
                                <button className="p-2 hover:bg-gray-300 rounded-full">
                                  <Edit2Icon className="h-4 w-4 text-gray-500" />
                                </button>
                              </Link>
                            ) : (
                              <ActionDropdown
                                student={student._id}
                                studentName={student.fname}
                                studentLastName={student.lname}
                              />
                            )}
                          </td>
                        </>
                      ) : (
                        <></>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {user?.role === "admin" && (
        <FeeSummaryCard
          pendingFees={totalAmounts?.payable - totalAmounts?.paid}
          receivedFees={totalAmounts?.paid}
        />
      )}

      {/* {/ {/ Pagination Section /} /} */}
      <div className="flex items-center justify-center gap-10">
        <Button
          onClick={handlePreviousPage}
          disabled={currentPage === 1}
          className="px-5 py-1 text-sm border bg-[#22C55E] rounded-md hover:bg-green-400 transition-colors disabled:opacity-50"
        >
          Previous
        </Button>
        <p className="text-sm text-gray-500">
          Showing {Math.min(currentPage * studentsPerPage, totalStudents)} of{" "}
          {totalStudents} students
        </p>
        <Button
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className="px-10 py-1 text-sm border bg-[#22C55E] rounded-md hover:bg-green-400 transition-colors disabled:opacity-50"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default StudentList;
